import { useMutation, useQuery } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Grid, Stack, TextField, Typography } from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import * as z from 'zod';
import { useToast } from "../../common/hooks/useToast";
import { ColumnInterface, Table } from "../../common/table/Table";
import { Item } from "../../styles/common";
import { Filter, UserFilters } from "../users/UserFilters";
import { USERS } from "../users/users.queries";
import { SEND_SMS } from "./sms.mutation";
import { config } from "../../config";

const columns: Array<ColumnInterface> = [
  { label: 'Username', key: 'username' },
  { label: 'First Name', key: 'firstName', textAlign: 'right' },
  { label: 'Last Name', key: 'lastName', textAlign: 'right' },
  { label: 'Phone Number', key: 'phone', textAlign: 'right' },
  { label: 'Email', key: 'email', textAlign: 'right' },
];

const perPage = config.rowsPerPage;

const schema = z.object({
  body: z.string().min(1, { message: 'Required' }),
});

type SchemaProps = z.infer<typeof schema>;

export const SmsWithFilters : React.FC = (): JSX.Element => {
  let variables = useRef<any>({
    "pagination": {
        "skip": 0,
        "take": perPage
    }
  });

  const { data, loading, refetch } = useQuery(USERS, { variables });

  const [page, setPage] = useState(0);
  const { success } = useToast();
  const [rowsPerPage, setRowsPerPage] = useState(perPage);
  const [smsSent, setSmsSent] = useState(false);

  useEffect(() => {
    variables.current = {
        pagination: {
            take: rowsPerPage,
            skip: page * rowsPerPage,
        }
    };

    refetch(variables.current);
}, [page, refetch, rowsPerPage]);

  const onFilter = useCallback((filter: Filter) => {
    variables.current = {
      ...variables.current,
      filter: {
          fieldFilter: {
              filters: filter.filters,
              operator: filter.operator,
          },
          hasReferred: filter.usersThatHaveReferred,
          hasRecommendedABusiness: filter.usersThatHaveRecommended,
          hasRecommendedSameBusiness: filter.usersThatHaveRecommendedSameBusiness,
          visitedPage: filter.visitedPage,
          hasSharedReferralLink: filter.hasSharedReferralLink,
      },
  };
    refetch(variables.current);
  }, [refetch]);

  const [sendSms, result] = useMutation(SEND_SMS, {
    errorPolicy: "all",
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SchemaProps>({
    resolver: zodResolver(schema),
  });

  const submitData = useCallback((values: any) => {
    console.log('submit', values, variables.current);
    sendSms({
      variables: {
        filter: variables.current.filter,
        message: values.body,
      }
    });
    setSmsSent(true);
  }, [sendSms]);

  useEffect(() => {
    if (result.data && smsSent) {
      success("SMS sent successfully.");
      reset();
      setSmsSent(false);
    }
  }, [smsSent, reset, result.data, success]);

  if (loading) return <h4>Loading...</h4>;

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Item>
            <Typography sx={{ m: 2 }} variant="h4">Send SMS</Typography>
          </Item>
        </Grid>
        <Grid item xs={12}>
          <Item>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
              >
                Select users. Current selection: {data?.fetchUsers?.total ?? 0}
              </AccordionSummary>
              <AccordionDetails>
                <UserFilters onFilter={onFilter} />
                <Table
                  page={page}
                  setPage={setPage}
                  rowsPerPage={rowsPerPage}
                  setRowsPerPage={setRowsPerPage}
                  loading={loading}
                  columns={[
                      ...columns,
                    ]}
                  rows={data.fetchUsers.users}
                  total={data.fetchUsers.total}
                />
              </AccordionDetails>
            </Accordion>
            <form
              autoComplete="off"
              noValidate
              onSubmit={handleSubmit((d) => submitData(d))}
            >
              <Stack spacing={2}>
                <Typography sx={{ m: 2 }} variant="h6">SMS content</Typography>
                <TextField label="Body" {...register('body')} error={!!errors.body} helperText={errors.body?.message} multiline minRows={3} />
              </Stack>

              <div style={{marginTop: '1em'}}>
                <Button size="large" sx={{ marginRight: 3 }} type="submit" variant="contained" color="success">
                    Send
                </Button>
              </div>
            </form>
          </Item>
        </Grid>
      </Grid>
    </Box>
  );
}